import { useState, useEffect } from 'react'
import { siteAPI, uploadFile } from '../services/api'
import { useNotification } from '../context/NotificationContext'
import { getErrorMessage, getImageUrl } from '../utils/helpers'

const About = () => {
  const { showSuccess, showError } = useNotification()
  const [settings, setSettings] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [previewImage, setPreviewImage] = useState(null)

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      const response = await siteAPI.getSettings()
      setSettings(response.data.settings || {})
    } catch (error) {
      showError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const handleTextChange = (e) => {
    const { name, value } = e.target
    setSettings(prev => ({ ...prev, [name]: value }))
  }

  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (ev) => setPreviewImage(ev.target.result)
      reader.readAsDataURL(file)
    } else {
      setPreviewImage(null)
    }
  }

  const handleImageUpload = async (e) => {
    e.preventDefault()
    const formData = new FormData(e.target)
    const imageFile = formData.get('image')
    const remove = formData.get('remove') === '1'

    setUploadingImage(true)
    try {
      if (remove) {
        await siteAPI.updateSettings({ about_image: '' })
        showSuccess('About image removed.')
        setPreviewImage(null)
      } else if (imageFile && imageFile.size > 0) {
        const uploadRes = await uploadFile(imageFile)
        const uploadedFilename = uploadRes.data.filename || uploadRes.data.image
        if (!uploadedFilename) {
          throw new Error('Upload succeeded but no filename was returned.')
        }
        await siteAPI.updateSettings({ about_image: uploadedFilename })
        showSuccess('About image updated successfully.')
        setPreviewImage(null)
      } else {
        showError('Please choose an image file to upload.')
        setUploadingImage(false)
        return
      }
      await fetchSettings()
      e.target.reset()
    } catch (error) {
      showError(getErrorMessage(error))
    } finally {
      setUploadingImage(false)
    }
  }

  const handleContentSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    const formData = new FormData(e.target)
    const data = Object.fromEntries(formData.entries())

    try {
      await siteAPI.updateSettings(data)
      showSuccess('About Us content saved successfully!')
      await fetchSettings()
    } catch (error) {
      showError(getErrorMessage(error))
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="admin-page">Loading...</div>

  const currentAboutImg = previewImage || (settings.about_image ? getImageUrl(settings.about_image) : '')

  return (
    <div className="admin-page">
      <h2 className="admin-title">About Us Management</h2>
      <p className="admin-sub">
        Manage the About Us image, story text, experience badge, highlights, stats counter, and why choose us section.
        Changes appear on the website immediately.
      </p>

      <div className="row">
        {/* Left Column: Image & Badge */}
        <div className="col-lg-5">
          {/* About Image Panel */}
          <div className="panel">
            <div className="panel-head">
              <i className="fa fa-picture-o" style={{marginRight: '8px', color: 'var(--gold-3)'}}></i>
              About Us Image
            </div>
            
            <div className="file-preview" style={{textAlign: 'center', minHeight: '160px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.02)', padding: '16px', borderRadius: '8px', marginBottom: '16px', border: '1px dashed var(--line)'}}>
              {currentAboutImg ? (
                <>
                  <span style={{fontSize: '12px', color: 'var(--muted)', marginBottom: '8px'}}>
                    {previewImage ? 'Preview of Selected Image:' : 'Current About Image:'}
                  </span>
                  <img
                    src={currentAboutImg}
                    alt="About Us"
                    style={{width: '100%', maxHeight: '220px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--line)'}}
                  />
                  {!previewImage && settings.about_image && (
                    <small style={{color: 'var(--muted)', marginTop: '6px', wordBreak: 'break-all'}}>
                      {settings.about_image}
                    </small>
                  )}
                </>
              ) : (
                <div style={{color: 'var(--muted)', textAlign: 'center'}}>
                  <i className="fa fa-image fa-3x" style={{opacity: 0.3, marginBottom: '8px', display: 'block'}}></i>
                  <span>No image uploaded yet.</span>
                </div>
              )}
            </div>

            <form onSubmit={handleImageUpload}>
              <div className="form-group">
                <label>Upload New About Image</label>
                <input
                  type="file"
                  name="image"
                  className="form-control-file"
                  accept="image/*"
                  onChange={handleImageFileChange}
                />
              </div>

              {settings.about_image && (
                <div className="form-group mb-2">
                  <label className="checkbox-inline" style={{cursor: 'pointer'}}>
                    <input type="checkbox" name="remove" value="1" /> Remove current image
                  </label>
                </div>
              )}

              <button type="submit" className="btn btn-gold btn-block" disabled={uploadingImage}>
                <i className={`fa ${uploadingImage ? 'fa-spinner fa-spin' : 'fa-cloud-upload'}`}></i>
                {' '}{uploadingImage ? 'Uploading Image...' : 'Save & Upload Image'}
              </button>
            </form>
            <p className="muted mt-3" style={{fontSize: '12px', lineHeight: 1.4}}>
              This image is displayed on both the <strong>Homepage About Section</strong> and the dedicated <strong>About Us Page</strong>.
            </p>
          </div>

          {/* Experience Badge Panel */}
          <div className="panel mt-3">
            <div className="panel-head">
              <i className="fa fa-certificate" style={{marginRight: '8px', color: 'var(--gold-3)'}}></i>
              Experience Badge
            </div>
            <form onSubmit={handleContentSubmit}>
              <div className="row">
                <div className="col-sm-5">
                  <div className="form-group">
                    <label>Badge Number</label>
                    <input
                      type="text"
                      name="hero_badge_num"
                      className="form-control"
                      value={settings.hero_badge_num || ''}
                      onChange={handleTextChange}
                      placeholder="15"
                    />
                  </div>
                </div>
                <div className="col-sm-7">
                  <div className="form-group">
                    <label>Badge Text</label>
                    <input
                      type="text"
                      name="about_badge_text"
                      className="form-control"
                      value={settings.about_badge_text || ''}
                      onChange={handleTextChange}
                      placeholder="Years of Artistry"
                    />
                  </div>
                </div>
              </div>
              <button type="submit" className="btn btn-gold btn-sm btn-block" disabled={saving}>
                <i className="fa fa-check"></i> Save Badge Settings
              </button>
            </form>
          </div>

          {/* Statistics Band Panel */}
          <div className="panel mt-3">
            <div className="panel-head">
              <i className="fa fa-bar-chart" style={{marginRight: '8px', color: 'var(--gold-3)'}}></i>
              Stats Counter Band
            </div>
            <form onSubmit={handleContentSubmit}>
              <div className="row">
                <div className="col-6">
                  <div className="form-group">
                    <label>Stat 1 Number</label>
                    <input type="text" name="stat_1_num" className="form-control" value={settings.stat_1_num || ''} onChange={handleTextChange} placeholder="15" />
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group">
                    <label>Stat 1 Label</label>
                    <input type="text" name="stat_1_label" className="form-control" value={settings.stat_1_label || ''} onChange={handleTextChange} placeholder="Years Experience" />
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group">
                    <label>Stat 2 Number</label>
                    <input type="text" name="stat_2_num" className="form-control" value={settings.stat_2_num || ''} onChange={handleTextChange} placeholder="2500" />
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group">
                    <label>Stat 2 Label</label>
                    <input type="text" name="stat_2_label" className="form-control" value={settings.stat_2_label || ''} onChange={handleTextChange} placeholder="Happy Clients" />
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group">
                    <label>Stat 3 Number</label>
                    <input type="text" name="stat_3_num" className="form-control" value={settings.stat_3_num || ''} onChange={handleTextChange} placeholder="12" />
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group">
                    <label>Stat 3 Label</label>
                    <input type="text" name="stat_3_label" className="form-control" value={settings.stat_3_label || ''} onChange={handleTextChange} placeholder="Expert Stylists" />
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group">
                    <label>Stat 4 Number</label>
                    <input type="text" name="stat_4_num" className="form-control" value={settings.stat_4_num || ''} onChange={handleTextChange} placeholder="40" />
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group">
                    <label>Stat 4 Label</label>
                    <input type="text" name="stat_4_label" className="form-control" value={settings.stat_4_label || ''} onChange={handleTextChange} placeholder="Beauty Awards" />
                  </div>
                </div>
              </div>
              <button type="submit" className="btn btn-gold btn-sm btn-block" disabled={saving}>
                <i className="fa fa-check"></i> Save Stats
              </button>
            </form>
          </div>
        </div>

        {/* Right Column: Story Text, Highlights, Why Choose Us */}
        <div className="col-lg-7">
          {/* Main Story Panel */}
          <div className="panel">
            <div className="panel-head">
              <i className="fa fa-file-text-o" style={{marginRight: '8px', color: 'var(--gold-3)'}}></i>
              About Us Story &amp; Headings
            </div>
            <form onSubmit={handleContentSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Page Kicker / Subtitle</label>
                    <input
                      type="text"
                      name="about_page_kicker"
                      className="form-control"
                      value={settings.about_page_kicker || ''}
                      onChange={handleTextChange}
                      placeholder="Our Story"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>About Page Title</label>
                    <input
                      type="text"
                      name="about_page_title"
                      className="form-control"
                      value={settings.about_page_title || ''}
                      onChange={handleTextChange}
                      placeholder="About Us"
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Homepage Section Title</label>
                    <input
                      type="text"
                      name="home_about_title"
                      className="form-control"
                      value={settings.home_about_title || ''}
                      onChange={handleTextChange}
                      placeholder="About Us"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>About Story Main Heading</label>
                    <input
                      type="text"
                      name="about_main_title"
                      className="form-control"
                      value={settings.about_main_title || ''}
                      onChange={handleTextChange}
                      placeholder="A Legacy of Beauty"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Story / Intro Content (Separate paragraphs with double newlines)</label>
                <textarea
                  name="about_intro"
                  className="form-control"
                  rows="7"
                  value={settings.about_intro || ''}
                  onChange={handleTextChange}
                  placeholder="Founded more than a decade and a half ago, HEET7 Studio began with a simple belief..."
                ></textarea>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>CTA Button Text</label>
                    <input
                      type="text"
                      name="about_cta_text"
                      className="form-control"
                      value={settings.about_cta_text || ''}
                      onChange={handleTextChange}
                      placeholder="Visit Us Today"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Homepage Kicker</label>
                    <input
                      type="text"
                      name="home_about_kicker"
                      className="form-control"
                      value={settings.home_about_kicker || ''}
                      onChange={handleTextChange}
                      placeholder="Our Story"
                    />
                  </div>
                </div>
              </div>

              <button type="submit" className="btn btn-gold btn-lg btn-block mt-2" disabled={saving}>
                <i className="fa fa-save"></i> {saving ? 'Saving...' : 'Save Story Content'}
              </button>
            </form>
          </div>

          {/* About Highlights Panel */}
          <div className="panel mt-3">
            <div className="panel-head">
              <i className="fa fa-star-o" style={{marginRight: '8px', color: 'var(--gold-3)'}}></i>
              About Page Feature Highlights (3 Key Points)
            </div>
            <form onSubmit={handleContentSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Highlight 1 Title (e.g. Award Winning)</label>
                    <input type="text" name="about_page_hl1_title" className="form-control" value={settings.about_page_hl1_title || ''} onChange={handleTextChange} placeholder="Award Winning Studio" />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Highlight 1 Description</label>
                    <input type="text" name="about_page_hl1_text" className="form-control" value={settings.about_page_hl1_text || ''} onChange={handleTextChange} placeholder="Recognised across the region for excellence." />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Highlight 2 Title (e.g. Premium Products)</label>
                    <input type="text" name="about_page_hl2_title" className="form-control" value={settings.about_page_hl2_title || ''} onChange={handleTextChange} placeholder="Consciously Premium" />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Highlight 2 Description</label>
                    <input type="text" name="about_page_hl2_text" className="form-control" value={settings.about_page_hl2_text || ''} onChange={handleTextChange} placeholder="Sustainable, cruelty-free luxury products." />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Highlight 3 Title (e.g. Client First)</label>
                    <input type="text" name="about_page_hl3_title" className="form-control" value={settings.about_page_hl3_title || ''} onChange={handleTextChange} placeholder="Client First Promise" />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Highlight 3 Description</label>
                    <input type="text" name="about_page_hl3_text" className="form-control" value={settings.about_page_hl3_text || ''} onChange={handleTextChange} placeholder="Your comfort and confidence drive every decision." />
                  </div>
                </div>
              </div>

              <button type="submit" className="btn btn-gold btn-sm btn-block" disabled={saving}>
                <i className="fa fa-check"></i> Save Highlights
              </button>
            </form>
          </div>

          {/* Why Choose Us Panel */}
          <div className="panel mt-3">
            <div className="panel-head">
              <i className="fa fa-diamond" style={{marginRight: '8px', color: 'var(--gold-3)'}}></i>
              "Why Choose Us" Section
            </div>
            <form onSubmit={handleContentSubmit}>
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Kicker</label>
                    <input type="text" name="why_kicker" className="form-control" value={settings.why_kicker || ''} onChange={handleTextChange} placeholder="Why choose us" />
                  </div>
                </div>
                <div className="col-md-8">
                  <div className="form-group">
                    <label>Main Title</label>
                    <input type="text" name="why_title" className="form-control" value={settings.why_title || ''} onChange={handleTextChange} placeholder="The HEET7 Studio Difference" />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Subtitle / Description</label>
                <input type="text" name="why_sub" className="form-control" value={settings.why_sub || ''} onChange={handleTextChange} placeholder="Three promises we make to every guest who walks through our doors." />
              </div>

              <div style={{background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '6px', marginBottom: '12px', border: '1px solid var(--line)'}}>
                <h6 style={{color: 'var(--gold-3)', marginBottom: '8px'}}>Promise 1</h6>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Title</label>
                      <input type="text" name="why_1_title" className="form-control form-control-sm" value={settings.why_1_title || ''} onChange={handleTextChange} placeholder="Bespoke Every Time" />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Tag / Badge</label>
                      <input type="text" name="why_1_price" className="form-control form-control-sm" value={settings.why_1_price || ''} onChange={handleTextChange} placeholder="Always personal" />
                    </div>
                  </div>
                </div>
                <div className="form-group mb-0">
                  <label>Description</label>
                  <textarea name="why_1_text" className="form-control form-control-sm" rows="2" value={settings.why_1_text || ''} onChange={handleTextChange} placeholder="No cookie-cutter looks. Every style is designed around your features..."></textarea>
                </div>
              </div>

              <div style={{background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '6px', marginBottom: '12px', border: '1px solid var(--line)'}}>
                <h6 style={{color: 'var(--gold-3)', marginBottom: '8px'}}>Promise 2</h6>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Title</label>
                      <input type="text" name="why_2_title" className="form-control form-control-sm" value={settings.why_2_title || ''} onChange={handleTextChange} placeholder="Certified Experts" />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Tag / Badge</label>
                      <input type="text" name="why_2_price" className="form-control form-control-sm" value={settings.why_2_price || ''} onChange={handleTextChange} placeholder="Always certified" />
                    </div>
                  </div>
                </div>
                <div className="form-group mb-0">
                  <label>Description</label>
                  <textarea name="why_2_text" className="form-control form-control-sm" rows="2" value={settings.why_2_text || ''} onChange={handleTextChange} placeholder="Continuously trained artists who keep up with global trends..."></textarea>
                </div>
              </div>

              <div style={{background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '6px', marginBottom: '12px', border: '1px solid var(--line)'}}>
                <h6 style={{color: 'var(--gold-3)', marginBottom: '8px'}}>Promise 3</h6>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Title</label>
                      <input type="text" name="why_3_title" className="form-control form-control-sm" value={settings.why_3_title || ''} onChange={handleTextChange} placeholder="Luxury within Reach" />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Tag / Badge</label>
                      <input type="text" name="why_3_price" className="form-control form-control-sm" value={settings.why_3_price || ''} onChange={handleTextChange} placeholder="Always honest" />
                    </div>
                  </div>
                </div>
                <div className="form-group mb-0">
                  <label>Description</label>
                  <textarea name="why_3_text" className="form-control form-control-sm" rows="2" value={settings.why_3_text || ''} onChange={handleTextChange} placeholder="Five-star service and premium products, with transparent pricing..."></textarea>
                </div>
              </div>

              <button type="submit" className="btn btn-gold btn-sm btn-block" disabled={saving}>
                <i className="fa fa-check"></i> Save Why Choose Us
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default About
